import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { DataSource } from './datasource';
import { MockDataSource } from './mock';
import { SupabaseDataSource } from './supabase';
import type { CurrentUser } from './types';

/**
 * Chooses the backend once, at startup.
 *
 * Falling back to the mock when Supabase isn't configured is what lets the admin run and
 * be reviewed on a machine with no Docker. The choice is surfaced in the sidebar so it is
 * never ambiguous which one you are looking at.
 */
function createDataSource(): DataSource {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (url && key) return new SupabaseDataSource(url, key);
    return new MockDataSource();
}

interface AppState {
    data: DataSource;
    user: CurrentUser | null;
    setUser: (user: CurrentUser | null) => void;
    ready: boolean;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
    const [data] = useState(createDataSource);
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        data.currentUser()
            .then(setUser)
            .catch(() => setUser(null))
            .finally(() => setReady(true));
    }, [data]);

    return (
        <AppContext.Provider value={{ data, user, setUser, ready }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp(): AppState {
    const context = useContext(AppContext);
    if (!context) throw new Error('useApp must be used inside AppProvider');
    return context;
}

/** Load-once async helper with the loading/error shape every screen renders. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]) {
    const [state, setState] = useState<{ loading: boolean; error: string | null; data: T | null }>({
        loading: true, error: null, data: null,
    });
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState((s) => ({ ...s, loading: true, error: null }));
        load()
            .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
            .catch((error: unknown) => {
                if (!cancelled) {
                    setState({
                        loading: false,
                        error: error instanceof Error ? error.message : String(error),
                        data: null,
                    });
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce]);

    return { ...state, reload: () => setNonce((n) => n + 1) };
}
